import React, { useState, useEffect } from "react";
import axios from "axios";
import AuthHeader from "../../../UI/AuthHeader/AuthHeader";
import { useNavigate } from "react-router-dom";
import { useDispatch } from 'react-redux';
import { Modal } from "../../../UI/Modal/Modal";
import Rules from "../../Rules";
import BubbleFloat from "../../../UI/BubbleFloat/BubbleFloat";
import { checkAuthenticatedUser, startExamSession } from "../../../../utils/client-functions";
import { useErrorSetter } from '../../../../hooks/useUiMessages';
import useExamStartGate from "../../../../hooks/useExamStartGate";
import ExamReadyModal from "../../ExamReadyModal";

// Federal Fire Service (F.F.S) Recruitment CBT mock
// Implements full single-paper CBT flow similar to NCS/NIS components

const Ffs = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [authMenuOpen, setAuthMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progressStatus, setProgressStatus] = useState('Gathering Exam Details...');
  const [progressPercentage, setProgressPercentage] = useState(0);
  const { examReady, handleOngoing, handleDoneEvent, confirmStartExam, closeGate } = useExamStartGate(navigate, dispatch);

  const isAuth = localStorage?.getItem("token");
  const errorSetter = useErrorSetter();

  const toggleAuthMenu = () => setAuthMenuOpen(!authMenuOpen);

    useEffect(() => {
        const verifyAuth = async () => {
            try {
                await checkAuthenticatedUser();
            } catch (error) {
                errorSetter("Please login to access mock exams");
                sessionStorage.setItem("prevPath", window.location.pathname);
                navigate("/popin");
            }
        };
        verifyAuth();
    }, [navigate, errorSetter]);

  const ffsExamMeta = {
    subject: "Federal Fire Service (Nigeria) (F.F.S) Recruitment CBT",
    // Duration: 60 minutes (3600 seconds). Backend expects seconds.
    timeAlloted: 60 * 60,
    totalQuestions: 60,
    passMarkPercent: 60,
    passMarkRaw: 36,
    description: `Federal Fire Service (Nigeria) (F.F.S) Recruitment/ Aptitude Test — practice mock.

    Blueprint & Focus Areas:
    - English Language
    - Mathematics
    - General Studies
    - Current Affairs

    Structure & Format:
    - Single CBT paper of 60 multiple-choice questions (4 options).
    - Holistic adaptive presentation (questions shuffled). No diagrams; emphasis on scenario-based practicals.
    - Duration: 60 minutes total (no strict per-subject timing).
    - Pass mark: 60% (36/60). Answer all; flag and review allowed.

    Rules & Proctoring (practice approximation):
    - AI-proctored (webcam/microphone monitoring, environment scan, eye-tracking). Malpractice leads to disqualification.
    - Quiet room, stable internet. Use Chrome on laptop/desktop; no VPNs or secondary devices.
    - Login at scheduled slot; present valid ID (NIN/SSCE). Follow proctor instructions.

    This mock mirrors the full F.F.S recruitment CBT flow (AI-proctoring, grading, and insights). Use it for timed practice and pattern familiarization.`,
  };

  const closeModalsAndReset = () => {
    navigate('/user/dashboard/mock');
  };

  const handleStartExam = async () => {
    try {
      await checkAuthenticatedUser();
    } catch (error) {
      errorSetter("Please login to generate an exam");
            sessionStorage.setItem("prevPath", window.location.pathname);
            return navigate("/popin");
    }

    setLoading(true);
    setProgressStatus("Gathering Exam Details...");
    setProgressPercentage(5);

    const payload = {
      subject: ffsExamMeta.subject,
      timeAlloted: ffsExamMeta.timeAlloted,
      description: ffsExamMeta.description,
      examName: "Federal Fire Service (Nigeria)",
      totalQuestions: ffsExamMeta.totalQuestions,
      passMarkPercent: ffsExamMeta.passMarkPercent,
    };

    try {
      await startExamSession({
        payload,
        tokenOverride: isAuth,
        onOngoing: (examDetails) => { setLoading(false); handleOngoing(examDetails); },
        onDone: (e) => { handleDoneEvent(e); },
        onError: (msg) => { setLoading(false); errorSetter(msg); },
        setLoading, setProgressStatus, setProgressPercentage
      });
    } catch (error) {
      setLoading(false);
      errorSetter(error?.response?.data?.message || error?.response?.data?.error || "An Error Occurred. Please try again. If issue persists, please chat with us.");
      if (error?.response?.data?.openPricing) {
        window.dispatchEvent(new CustomEvent('openExamPricing'));
      }
    }
  };

  return (
    <div className="auth-container">
      <BubbleFloat />
      <div className="auth-container-inner content">
        <AuthHeader
          authMenuOpen={authMenuOpen}
          onClick={toggleAuthMenu}
          headerText="Federal Fire Service (Nigeria) MOCK"
        />

        <div className="BodyWrapper">
          <div className="BuildNavigator">
            <div className="ActiveNav"><span>1</span>Selection</div>
            <div><span>2</span>Simulation</div>
            <div><span>3</span>Insights & Tutor</div>
          </div>

          <div style={{ padding: '20px 5px' }}>
            <h4>Federal Fire Service (Nigeria) Mock</h4>
            <p style={{whiteSpace: 'pre-wrap'}}>{ffsExamMeta.description.split('\n\n')[0]}</p>
            <div style={{ marginTop: 12 }}>
              <button className="ButtonSubmitBlack" onClick={handleStartExam}>Start Federal Fire Service (Nigeria) Mock</button>
            </div>
          </div>
        </div>
      </div>

      {!examReady && (
        <Rules
          examName={"Federal Fire Service (Nigeria) Recruitment CBT"}
          closeModalsAndReset={closeModalsAndReset}
          subject={ffsExamMeta}
          handleStartExam={handleStartExam}
        />
      )}

      {loading && (
        <Modal
          header4={`PROCESSING... DO NOT RELOAD`}
          header3={progressStatus}
          progress={progressPercentage}
        />
      )}
      {examReady && (
        <ExamReadyModal
          isOpen={examReady}
          onClose={closeGate}
          onStart={confirmStartExam}
          examLabel="Federal Fire Service (Nigeria)"
        />
      )}
    </div>
  );
};

export default Ffs;

